import React, { useEffect, useRef, useState } from "react";
import { MdArrowBack } from "react-icons/md";
import { Exercise } from "../../../../../models/Exercise";
import { Assort } from "../../../../../models/Assort";
import { TicketAccess } from "../../../../../models/TicketAccess";
import { BasketItem } from "../../../../../models/BasketItem";
import { BsBasket3 } from "react-icons/bs";
import EAAlert from "../../../../shared/EAAlert";
import EASkeletonLoader from "../../../../shared/EASkeletonLoader";
import EAButton from "../../../../shared/Buttons/EAButton";
import { useTranslation } from "react-i18next";
import TicketCard from "./TicketCard";
import "./CourtTicketSelection.scss";
import useCourtTicketSelection from "./useCourtTicketSelection";
import { SaleStep } from "../../../../../store/stepSaleConfig/stepSaleConfigReducer";
import EAModal from "../../../../shared/Modal/EAModal";

interface IProps {
  exerciseId: number;
  exerciseDetails: Exercise | null;
  tickets: Assort[];
  ticketAccess: TicketAccess;
  ticketLimit: number;
  onTicketsAdded: () => void;
  onBack: () => void;
  saleStep?: SaleStep;
}

const CourtTicketSelection: React.FC<IProps> = ({
  exerciseId,
  exerciseDetails,
  tickets: passedTickets,
  ticketAccess,
  ticketLimit,
  onTicketsAdded,
  onBack,
  saleStep,
}) => {
  const { t } = useTranslation();

  const {
    formatTimeRange,
    getQnt,
    getEffectiveMax,
    handleQuantityChange,
    addItemToBasket,
    removeItemFromBasket,
    sumBasket,
    saveBasket,
    alert,
    setAlert,
    openTooltip,
    setOpenTooltip,
    preBasket,
    ticketsInCurrentBasket,
  } = useCourtTicketSelection(exerciseId, ticketAccess, ticketLimit, onTicketsAdded);

  // Scroll shadow handling for tickets list
  const ticketsListRef = useRef<HTMLDivElement | null>(null);
  const [hasTopShadow, setHasTopShadow] = useState(false);
  const [hasBottomShadow, setHasBottomShadow] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [showLimitAlert, setShowLimitAlert] = useState(false);

  const updateShadows = () => {
    const el = ticketsListRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    setHasTopShadow(scrollTop > 0);
    setHasBottomShadow(scrollTop + clientHeight < scrollHeight - 1);
  };

  useEffect(() => {
    updateShadows();
    const el = ticketsListRef.current;
    if (el) {
      const hasOverflow = el.scrollHeight > el.clientHeight + 1;
      setShowScrollHint(hasOverflow);
    }
  }, [passedTickets.length]);

  if (!exerciseDetails || passedTickets.length === 0) {
    return <EASkeletonLoader height="200px" />;
  }

  const handleOnDecrement = (ticket: Assort, ticketAccess: TicketAccess) => {
    setShowLimitAlert(false);
    removeItemFromBasket(ticket, ticketAccess);
  };

  return (
    <div className="court-ticket-selection">
      <div className="court-ticket-selection__summary">
        <h4>{exerciseDetails.name}</h4>
        <b>
          {" "}
          <h5>
            {" "}
            {new Date(exerciseDetails.startTime).toLocaleDateString("pl-PL", {
              weekday: "long",
            })}
          </h5>
          |<h5>{new Date(exerciseDetails.startTime).toLocaleDateString("pl-PL")}</h5> |
          <h5>{formatTimeRange(exerciseDetails.startTime, exerciseDetails.closeTime)}</h5>
        </b>
      </div>

      {alert.length > 2 && alert !== t("dodano_bilet_do_koszyka") && <EAAlert type="error" message={alert} />}

      <div
        ref={ticketsListRef}
        className={
          `court-ticket-selection__tickets` +
          (hasTopShadow ? " has-top-shadow" : "") +
          (hasBottomShadow ? " has-bottom-shadow" : "")
        }
        onScroll={(e) => {
          updateShadows();
          const el = e.currentTarget as HTMLDivElement;
          if (el.scrollTop > 0 && showScrollHint) {
            setShowScrollHint(false);
          }
        }}
      >
        {passedTickets.map((item, index) => {
          const currentQnt = getQnt(item);
          const effectiveMax = getEffectiveMax(item);

          return (
            <TicketCard
              key={index}
              ticket={item}
              currentQuantity={currentQnt}
              effectiveMax={effectiveMax}
              onQuantityChange={handleQuantityChange}
              onIncrement={addItemToBasket}
              onDecrement={(ticket, ticketAccess) => handleOnDecrement(ticket, ticketAccess)}
              ticketAccess={ticketAccess}
              openTooltip={openTooltip}
              setOpenTooltip={setOpenTooltip}
              onIncrementDisabledClick={() => setShowLimitAlert(true)}
            />
          );
        })}
        {showScrollHint && (
          <div className="court-ticket-selection__scroll-hint">
            <span>Przewiń, aby zobaczyć więcej biletów</span>
          </div>
        )}
      </div>

      {ticketLimit === ticketsInCurrentBasket() && showLimitAlert && (
        <EAAlert type="error" message={t("nie_mozesz_dodac_wiekszej_ilosci_biletow_do_koszyka")} />
      )}

      {saleStep?.showOtherTicketsPool && ticketLimit > 0 && ticketLimit !== ticketsInCurrentBasket() && (
        <EAAlert
          type="warning"
          message={`${t("na_to_wejscie_obowiazuje_limit_biletow_pozostalo")} ${
            ticketLimit - ticketsInCurrentBasket()
          } ${t("biletow")}`}
        />
      )}

      <div className="court-ticket-selection__footer">
        <div className="court-ticket-selection__total">
          {t("suma")}:{" "}
          <strong>
            {sumBasket().toFixed(2)} {t("zl")}
          </strong>
        </div>
        <div className="court-ticket-selection__actions">
          <EAButton
            title={t("dodaj_do_koszyka")}
            onClick={saveBasket}
            endIcon={<BsBasket3 />}
            variant="filled"
            color="primary"
            fullWidth
            disabled={preBasket.reduce((acc: number, item: BasketItem) => acc + item.qnt, 0) === 0}
          />
          <EAButton
            title="Powrót"
            onClick={() => {
              setAlert("");
              onBack();
            }}
            startIcon={<MdArrowBack />}
            variant="text"
            fullWidth
          />
        </div>
      </div>

      {openTooltip && (
        <EAModal
          isOpen={!!openTooltip}
          onClose={() => setOpenTooltip(null)}
          title={passedTickets.find((t) => t.name === openTooltip)?.name || ""}
          cancelButtonLabel={t("zamknij")}
          hideAcceptButton
        >
          <article
            dangerouslySetInnerHTML={{
              __html: passedTickets.find((t) => t.name === openTooltip)?.description || "",
            }}
          />
        </EAModal>
      )}
    </div>
  );
};

export default CourtTicketSelection;
